<script>
  import Breadcrumbs from '$components/Breadcrumbs/Breadcrumbs.svelte';
  import InfoTile from '$components/InfoTile/InfoTile.svelte';
  import { getPath } from '$utils/imageKit.ts';
  import contentMap from '$utils/ContentMapLayout';

  let { data } = $props();

  const defaultImageSrc = (image) => getPath(image, { w: 324, h: 243, bg: '#000000', fo: 'bottom' });
  const highResImageSrc = (image) => getPath(image, { w: 324, h: 243, bg: '#000000', fo: 'bottom' }, 2);
</script>

<svelte:head>
  <link rel="preconnect" href="https://ik.imagekit.io/" />
  {@html data.structuredData}
</svelte:head>

<div class="lg:flex lg:flex-wrap lg:justify-center shadow-content-well bg-gray-mercury lg:pb-8">
  <Breadcrumbs path={data.path} />
  <div class="max-w-6xl mx-auto">
    <!-- Main Content Section -->
    <article class="lg:flex lg:mt-8 lg:px-4">
      <div>
        <!-- Drug Info -->
        <section class="p-4 pb-6 md:py-8 lg:px-4 lg:py-6 xl:p-10 lg:shadow-y-sm bg-white">
          <h1 class="text-1.5xl lg:text-3xl mb-1 leading-tight font-medium text-blue-navy inline-block">
            {data.title}
          </h1>
          <h2 class="lg:text-lg text-gray-charcoal mb-4 md:mb-6">{@html data.pageAlternativesTitle}</h2>
          {#if data.images}
            {#each data.images as dose}
              <h2 class="text-blue-navy font-medium md:text-lg mt-6 mb-2">
                {data.formHeadingTextTemplate.replace(/{{FORM}}/, dose.form)}
              </h2>
              {#if data.ndcText && dose.ndc}
                <p class="text-sm text-gray-gunmetal mb-4">
                  {@html data.ndcText.replace(/{{CODE}}/, dose.ndc)}
                </p>
              {/if}
              <div class={`sm:grid sm:gap-4 sm:grid-cols-2 md:grid-cols-1`}>
                {#each dose.images as image, i}
                  <div
                    class="p-4 bg-white mb-4 sm:mb-0 rounded-lg focus:outline-none shadow-y-sm overflow-hidden border border-gray-light-silver"
                  >
                    <div class="md:flex">
                      {#if image && image.image && image.image.url && image.image.alt}
                        <div class="mb-4 md:mb-0 md:min-w-60 md:w-60 md:mr-6">
                          <img
                            class="object-contain w-full"
                            srcset={`${defaultImageSrc(image.image)}, ${highResImageSrc(image.image)} 2x`}
                            src={defaultImageSrc(image.image)}
                            alt={image.image.alt}
                            loading={i > 0 === true ? 'lazy' : 'eager'}
                            width="324"
                            height="243"
                          />
                        </div>
                      {/if}
                      <div>
                        <h2 class="md:text-lg leading-tight font-medium text-blue-navy mb-4">
                          {`${data.name} ${dose.form} ${dose.label}`}
                        </h2>
                        {#if image.description}
                          <p class="mb-4 text-base leading-snug">
                            {image.description.replace(', and is manufactured by None', '')}
                          </p>
                        {/if}
                        <table>
                          <tbody>
                            {#if image.color}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.colorLabel}</th
                                >
                                <td>{image.color}</td>
                              </tr>
                            {/if}
                            {#if image.flavor}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.flavorLabel}</th
                                >
                                <td>{image.flavor}</td>
                              </tr>
                            {/if}
                            {#if image.shape}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.shapeLabel}</th
                                >
                                <td>{image.shape}</td>
                              </tr>
                            {/if}
                            {#if image.face_marking}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.faceMarkingLabel}</th
                                >
                                <td>{image.face_marking}</td>
                              </tr>
                            {/if}
                            {#if image.rear_marking}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.rearMarkingLabel}</th
                                >
                                <td>{image.rear_marking}</td>
                              </tr>
                            {/if}
                            {#if image.manufacturer}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.manufacturerLabel}</th
                                >
                                <td>{image.manufacturer}</td>
                              </tr>
                            {/if}
                            {#if dose.label}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.doseLabel}</th
                                >
                                <td>{dose.label}</td>
                              </tr>
                            {/if}
                            {#if dose.form}
                              <tr>
                                <th class="text-xs text-gray-charcoal font-medium pr-4 text-left align-top pt-1"
                                  >{data.imageDetailsLabels.formLabel}</th
                                >
                                <td>{dose.form}</td>
                              </tr>
                            {/if}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            {/each}
          {/if}
        </section>
      </div>
      <!-- Aside: Promotional Stuff (lowest prices, ad banners -->
      <aside class="lg:w-88 lg:min-w-88 lg:ml-10">
        {#if data.infoTile}
          <section
            class="bg-blue-robin-egg shadow-content-well p-4 md:px-10 md:py-6 lg:p-0 lg:mb-8 lg:shadow-none lg:bg-white"
          >
            <InfoTile {...data.infoTile} rounded={true} usedInAside={true} />
          </section>
        {/if}
        {#if data.aside}
          {#each data.aside.imports as content}
            {@const SvelteComponent = contentMap[content.__typename]}
            <div class="lg:mb-8">
              <SvelteComponent {...content} />
            </div>
          {/each}
        {/if}
      </aside>
    </article>
  </div>
</div>
